import { createSlice, isAnyOf } from '@reduxjs/toolkit';

import UniqueId from '../../services/uid/UniqueId';

import {
  ActionMeta,
  OPERATION_FINISHED,
  OPERATION_STARTED,
} from './actionHandler';
import form from './form';

export interface OperationProgressState {
  readonly pendingCount: number;
  readonly formUpdatesCountMap: Record<UniqueId, number>;
}

const initialState: OperationProgressState = {
  pendingCount: 0,
  formUpdatesCountMap: {},
};

const formUpdatesCountMapDecrease = (
  state: OperationProgressState,
  operationFinished: boolean | undefined,
  formId: UniqueId
): void => {
  if (operationFinished) {
    state.formUpdatesCountMap[formId] = Math.max(
      state.formUpdatesCountMap[formId] - 1,
      0
    );
  }
  if (state.formUpdatesCountMap[formId] === 0) {
    delete state.formUpdatesCountMap[formId];
  }
};

const operationProgressSlice = createSlice({
  name: 'operationProgress',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addMatcher(
        isAnyOf(
          form.update,
          form.updateWithOutParams,
          form.runSchemaUpdater,
          form.runSingleUpdater
        ),
        (state, action) => {
          const { meta } = action as { meta?: ActionMeta };
          if (meta?.[OPERATION_STARTED]) {
            state.formUpdatesCountMap[action.payload.formId] =
              (state.formUpdatesCountMap[action.payload.formId] ?? 0) + 1;
          }
        }
      )
      .addMatcher(
        isAnyOf(
          form.updateSuccess,
          form.updateWithOutParamsSuccess,
          form.runSchemaUpdaterSuccess,
          form.runSingleUpdaterSuccess
        ),
        (state, action) => {
          const { meta } = action as { meta?: ActionMeta };
          formUpdatesCountMapDecrease(
            state,
            meta?.[OPERATION_FINISHED],
            action.payload.formId
          );
        }
      )
      .addMatcher(
        isAnyOf(
          form.updateFailure,
          form.updateWithOutParamsFailure,
          form.runSchemaUpdaterFailure,
          form.runSingleUpdaterFailure
        ),
        (state, action) => {
          const { meta } = action as { meta?: ActionMeta };
          formUpdatesCountMapDecrease(
            state,
            meta?.[OPERATION_FINISHED],
            meta?.request?.formId || ''
          );
        }
      )
      .addMatcher(
        (action): action is { meta: ActionMeta } => !!action.meta,
        (state, { meta }) => {
          if (meta[OPERATION_STARTED]) {
            state.pendingCount++;
          }
          if (meta[OPERATION_FINISHED]) {
            state.pendingCount = Math.max(state.pendingCount - 1, 0);
          }
        }
      );
  },
});

export const operationProgressReducer = operationProgressSlice.reducer;
